import { useCallback, useEffect, useRef } from "react";

/**
 * Saves a footprinting form when the step is left, but only once the form holds real data. Without
 * that check, leaving before the first payload lands sends an empty body and deletes the farmer's
 * saved rows.
 *
 * Call the returned function from two places: the effect that writes server data into state, and
 * the form's `onValuesChange`. antd does not fire `onValuesChange` for `setFieldsValue`, so it only
 * ever means real farmer input — which must be saved even on a step whose query never resolves.
 *
 * The flag is a ref rather than state because the cleanup has to read it at unmount time, not the
 * value captured when the effect was registered.
 *
 * Pass `hasNothingToLoad` for a step the farmer has not saved before — there is no payload to wait
 * for, so the first save must go through. It is read once, on mount, which is the only point at
 * which "nothing has been saved yet" is meaningful.
 */
export const useSaveFormOnUnmount = (save: (args?: never) => void, enabled: boolean, hasNothingToLoad = false) => {
  const hasFormBeenPopulated = useRef(hasNothingToLoad);

  useEffect(
    () => () => {
      if (enabled && hasFormBeenPopulated.current) {
        save();
      }
    },
    [enabled, save]
  );

  return useCallback(() => {
    hasFormBeenPopulated.current = true;
  }, []);
};
